import React, { useEffect, useRef, useState } from 'react';
import { FaTimes, FaChevronDown, FaChevronUp, FaUtensils, FaShoppingCart, FaTruck, FaCreditCard, FaUndo, FaLeaf, FaMobile, FaHeadset, FaSearch } from 'react-icons/fa';
import './LegalModal.css';

const FAQModal = ({ isOpen, onClose }) => {
  const modalRef = useRef(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      modalRef.current?.focus();
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const toggleCategory = (index) => {
    setActiveCategory(activeCategory === index ? null : index);
    setActiveQuestion(null);
  };

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setActiveQuestion(activeQuestion === key ? null : key);
  };

  const faqData = [
    {
      category: "About Mealzy Foods",
      icon: FaUtensils,
      color: "from-blue-500 to-indigo-600",
      questions: [
        {
          question: "What is Mealzy Foods?",
          answer: "Mealzy Foods is an all-in-one food and vegetable delivery app that brings fresh, delicious meals and farm-fresh vegetables directly to your doorstep. We offer everything from South Indian breakfasts to North Indian thalis, plus daily-fresh vegetables for your kitchen."
        },
        {
          question: "What makes Mealzy different from other food delivery apps?",
          answer: "Unlike other apps, Mealzy combines cooked meal delivery with fresh vegetable delivery in one platform. You can order your lunch and your daily vegetables together, saving time and delivery charges."
        }
      ]
    },
    {
      category: "Ordering & Menu",
      icon: FaShoppingCart,
      color: "from-green-500 to-teal-600",
      questions: [
        {
          question: "What types of food can I order?",
          answer: "We offer a wide variety including South Indian dishes (dosa, idli, vada, sambar), North Indian meals and thalis, quick snacks and combo meals, beverages (filter coffee, juices, buttermilk), and add-ons (chutneys, pickles, accompaniments)."
        },
        {
          question: "Do you deliver fresh vegetables daily?",
          answer: "Yes! We deliver hand-picked, farm-fresh vegetables daily. Our vegetables are sourced fresh from farms and markets, cleaned, and delivered in hygienic packaging."
        },
        {
          question: "Can I customize my food orders?",
          answer: "This feature is not currently available, but we plan to offer it in the future."//Absolutely! You can choose spice levels, add extra items, swap ingredients, and even create custom combo meals according to your taste preferences.
        },
        {
          question: "Is the menu updated regularly?",
          answer: "Yes, we update our menu daily with fresh options and seasonal specials to keep your dining experience exciting and varied."
        }
      ]
    },
    {
      category: "Delivery & Timing",
      icon: FaTruck,
      color: "from-purple-500 to-pink-600",
      questions: [
        {
          question: "What are your delivery hours?",
          answer: "We deliver fresh meals and vegetables throughout the day. Specific delivery hours may vary by location. Check the app for exact timings in your area."
        },
        {
          question: "How fast is the delivery?",
          answer: "We prioritize fast delivery while maintaining food quality. Hot meals are delivered temperature-safe, and vegetables arrive fresh. You can track your order in real-time through the app."
        },
        {
          question: "Can I track my order?",
          answer: "Yes! Our app provides real-time order tracking so you can monitor your food and vegetables from kitchen/market to your doorstep."
        },
        {
          question: "Which areas do you deliver to?",
          answer: "We're expanding our delivery network constantly. Enter your location in the app to check if we deliver to your area."
        }
      ]
    },
    {
      category: "Payment & Pricing",
      icon: FaCreditCard,
      color: "from-orange-500 to-red-600",
      questions: [
        {
          question: "What payment methods do you accept?",
          answer: "We accept multiple secure payment options including UPI (Google Pay, PhonePe, Paytm, etc.), Credit/Debit cards, Mobile wallets, and Cash on Delivery (COD)."
        },
        {
          question: "Do you offer any discounts or combo deals?",
          answer: "Yes! We regularly offer combo deals, first-time user discounts, and special offers. Check the app's offers section for current deals."
        },
        {
          question: "Are there any delivery charges?",
          answer: "Delivery charges vary by location and order value. We often waive delivery fees for orders above a certain amount. Check the app during checkout for exact charges."
        }
      ]
    },
    {
      category: "Returns & Refunds Policy",
      icon: FaUndo,
      color: "from-red-500 to-pink-600",
      questions: [
        {
          question: "Do you accept returns for food and vegetables?",
          answer: "No, we do not accept returns for food items and fresh vegetables due to their perishable nature and food safety regulations. All food items are prepared fresh and vegetables are sourced daily for immediate consumption."
        },
        {
          question: "Why don't you offer returns on food orders?",
          answer: "For health and safety reasons, we cannot accept returned food items or fresh vegetables as they are perishable products. Once delivered, these items cannot be resold or redistributed to maintain hygiene standards and prevent foodborne illnesses."
        },
        {
          question: "What if I receive a wrong order?",
          answer: "If you receive an incorrect order, please contact our customer support immediately. We will arrange for the correct order to be delivered at no extra cost. However, the incorrect order cannot be returned due to food safety policies."
        },
        {
          question: "Do you provide refunds for quality issues?",
          answer: "Yes, we provide refunds for genuine quality issues such as spoiled vegetables, cold food, or items that don't match the description. Contact support with details and photos, and we'll process your refund accordingly."
        }
      ]
    },
    {
      category: "Food Quality & Hygiene",
      icon: FaLeaf,
      color: "from-teal-500 to-green-600",
      questions: [
        {
          question: "How do you ensure food quality and hygiene?",
          answer: "We maintain strict hygiene standards with fresh ingredients sourced daily, clean hygienic food preparation, temperature-controlled delivery, regular quality checks, and hygienic packaging for all items."
        },
        {
          question: "How fresh are your vegetables?",
          answer: "Our vegetables are sourced fresh daily from farms and markets. They're hand-picked, cleaned, and delivered the same day to ensure maximum freshness."
        },
        {
          question: "Do you cater to dietary restrictions?",
          answer: "Yes, we offer various options including vegetarian, vegan, and Jain food choices. You can filter menu items based on your dietary preferences."
        }
      ]
    },
    {
      category: "App Usage & Technical",
      icon: FaMobile,
      color: "from-indigo-500 to-purple-600",
      questions: [
        {
          question: "Is the Mealzy Foods app free to download?",
          answer: "Yes, the Mealzy Foods app is completely free to download from the App Store or Google Play Store."
        },
        {
          question: "Can I schedule orders for later?",
          answer: "This feature is not currently available, but we plan to offer it in the future."
        },
        {
          question: "What if I want to modify or cancel my order?",
          answer: "This feature is not currently available, but we plan to offer it in the future."
        }
      ]
    },
    {
      category: "Support & Contact",
      icon: FaHeadset,
      color: "from-pink-500 to-rose-600",
      questions: [
        {
          question: "How do I contact customer support?",
          answer: "You can reach us through in-app chat support, help section in the app, email support, and phone support (number available in app)."
        },
        {
          question: "What if my order is delayed or incorrect?",
          answer: "If you face any issues with your order, immediately contact our customer support through the app's help section or call our support number. We'll resolve the issue promptly."
        },
        {
          question: "How can I provide feedback about my order?",
          answer: "You can rate and review your order directly in the app after delivery. Your feedback helps us improve our service quality."
        }
      ]
    }
  ];

  const filteredFaqData = faqData.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => 
        q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  if (!isOpen) return null;

  return (
    <div className="legal-modal-overlay" onClick={handleBackdropClick}>
      <div className="legal-modal-container" ref={modalRef} tabIndex={-1}>
        <div className="legal-modal-header">
          <h2>Frequently Asked Questions</h2>
          <button className="legal-modal-close" onClick={onClose} aria-label="Close modal">
            <FaTimes />
          </button>
        </div>
        
        <div className="legal-modal-body">
          <div className="faq-modal-intro">
            <p>Find answers to common questions about Mealzy Foods. Can't find what you're looking for? Our support team is always here to help.</p>
            
            {/* Search Bar */}
            <div className="faq-modal-search">
              <div className="faq-search-box">
                <FaSearch className="faq-search-icon" />
                <input
                  type="text"
                  placeholder="Search your questions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="faq-search-input"
                />
              </div>
            </div>
          </div>

          {/* FAQ Categories */}
          <div className="faq-modal-container">
            {filteredFaqData.map((category, categoryIndex) => {
              const IconComponent = category.icon;
              const isActiveCategory = activeCategory === categoryIndex;

              return (
                <div
                  key={categoryIndex}
                  className={`faq-modal-category ${isActiveCategory ? 'active' : ''}`}
                >
                  {/* Category Header */}
                  <div
                    className="faq-modal-category-header"
                    onClick={() => toggleCategory(categoryIndex)}
                  >
                    <div className="faq-modal-category-left">
                      <div className={`faq-modal-category-icon bg-gradient-to-br ${category.color}`}>
                        <IconComponent />
                      </div>
                      <div className="faq-modal-category-info">
                        <h3 className="faq-modal-category-title">{category.category}</h3>
                        <span className="faq-modal-category-count">
                          {category.questions.length} question{category.questions.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    <div className="faq-modal-category-toggle">
                      {isActiveCategory ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                  </div>

                  {/* Questions */}
                  <div className={`faq-modal-questions ${isActiveCategory ? 'expanded' : ''}`}>
                    {category.questions.map((faq, questionIndex) => {
                      const questionKey = `${categoryIndex}-${questionIndex}`;
                      const isActive = activeQuestion === questionKey;

                      return (
                        <div
                          key={questionIndex}
                          className={`faq-modal-item ${isActive ? 'active' : ''}`}
                        >
                          <div
                            className="faq-modal-question"
                            onClick={() => toggleQuestion(categoryIndex, questionIndex)}
                          >
                            <h4 className="faq-modal-question-text">{faq.question}</h4>
                            <div className="faq-modal-question-toggle">
                              {isActive ? <FaChevronUp /> : <FaChevronDown />}
                            </div>
                          </div>
                          <div className={`faq-modal-answer ${isActive ? 'expanded' : ''}`}>
                            <div className="faq-modal-answer-content">
                              <p>{faq.answer}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Important Note */}
          <div className="faq-modal-note">
            <p>
              <strong>Important Note:</strong> Due to the perishable nature of food and fresh vegetables, all sales are final. 
              We focus on ensuring quality before delivery to provide you with the best possible experience.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQModal;
